import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { propertyAPI, roomAPI, reservationAPI } from '@/services/api';
import { ChevronLeft, ChevronRight, Building, Calendar, Layers, User, Phone, Mail, LogIn, LogOut, DollarSign, ExternalLink } from 'lucide-react';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, differenceInDays, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const OTA_COLORS = {
  booking: '#003580',
  airbnb: '#FF5A5F',
  expedia: '#00355F',
  direct: '#16a34a',
  default: '#6366f1',
};

export default function MultiCalendarioPage() {
  const [properties, setProperties] = useState([]);
  const [propertyData, setPropertyData] = useState({}); // { propId: { rooms, reservations } }
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  // Reservation detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReservation, setDetailReservation] = useState(null);
  const [detailRoom, setDetailRoom] = useState(null);
  const [detailProperty, setDetailProperty] = useState(null);

  const openReservationDetail = (reservation, room, prop) => {
    setDetailReservation(reservation);
    setDetailRoom(room);
    setDetailProperty(prop);
    setDetailOpen(true);
  };

  const goToFullReservation = () => {
    if (detailProperty?.id) {
      localStorage.setItem('eden_selected_property', detailProperty.id);
    }
    navigate('/calendar');
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  useEffect(() => { fetchAllProperties(); }, []);
  useEffect(() => { if (properties.length > 0) fetchAllData(); }, [properties, currentDate]);

  useEffect(() => {
    // Auto-scroll to today
    if (scrollRef.current) {
      const todayIndex = days.findIndex(d => isToday(d));
      if (todayIndex > 0) {
        const cellWidth = 44;
        scrollRef.current.scrollLeft = todayIndex * cellWidth - 200;
      }
    }
  }, [propertyData]);

  const fetchAllProperties = async () => {
    try {
      const res = await propertyAPI.getAll();
      setProperties(res.data);
    } catch (e) { toast.error('Error al cargar propiedades'); }
    finally { setLoading(false); }
  };

  const fetchAllData = async () => {
    const data = {};
    for (const prop of properties) {
      try {
        const [roomsRes, resRes] = await Promise.all([
          roomAPI.getAll(prop.id),
          reservationAPI.getAll(prop.id),
        ]);
        data[prop.id] = { rooms: roomsRes.data, reservations: resRes.data };
      } catch (e) {
        data[prop.id] = { rooms: [], reservations: [] };
      }
    }
    setPropertyData(data);
  };

  const getReservationForCell = (roomId, day, reservations) => {
    return reservations.find(r => {
      if (r.estado === 'cancelada') return false;
      const start = parseISO(r.fecha_checkin);
      const end = parseISO(r.fecha_checkout);
      return r.room_id === roomId && day >= start && day <= end;
    });
  };

  const getReservationSpan = (reservation, day) => {
    const start = parseISO(reservation.fecha_checkin);
    const end = parseISO(reservation.fecha_checkout);
    const isStart = isSameDay(day, start);
    const isEnd = isSameDay(day, end);
    return { isStart, isEnd };
  };

  const getOccupancy = (propId, day) => {
    const data = propertyData[propId];
    if (!data) return 0;
    const totalRooms = data.rooms.length;
    if (totalRooms === 0) return 0;
    const occupied = data.rooms.filter(room => {
      return data.reservations.some(r => {
        if (r.estado === 'cancelada') return false;
        const start = parseISO(r.fecha_checkin);
        const end = parseISO(r.fecha_checkout);
        return r.room_id === room.id && day >= start && day <= end;
      });
    }).length;
    return Math.round((occupied / totalRooms) * 100);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="multi-calendario-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{fontFamily: 'Manrope'}}>
              <Layers className="h-7 w-7" />
              Multi-Calendario
            </h1>
            <p className="text-muted-foreground mt-1">
              Vista unificada de {properties.length} propiedad{properties.length !== 1 ? 'es' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-lg font-semibold min-w-[160px] text-center" style={{fontFamily: 'Manrope'}}>
              {format(currentDate, 'MMMM yyyy', { locale: es })}
            </span>
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())} data-testid="multi-cal-today">
              Hoy
            </Button>
          </div>
        </div>

        {/* Calendars for each property */}
        {properties.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No tiene propiedades registradas</p>
            </CardContent>
          </Card>
        ) : (
          properties.map((prop) => {
            const data = propertyData[prop.id] || { rooms: [], reservations: [] };
            return (
              <Card key={prop.id} className="overflow-hidden" data-testid={`property-calendar-${prop.id}`}>
                <CardHeader className="pb-2 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg" style={{fontFamily: 'Manrope'}}>
                      <Building className="h-5 w-5" />
                      {prop.nombre}
                      <Badge variant="outline" className="ml-2">{prop.ciudad}</Badge>
                      <Badge variant="outline">{data.rooms.length} hab.</Badge>
                    </CardTitle>
                    <Badge className="bg-blue-100 text-blue-700">
                      {data.reservations.filter(r => r.estado !== 'cancelada').length} reservas
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto" ref={scrollRef}>
                    <table className="w-full border-collapse text-xs">
                      <thead>
                        <tr>
                          <th className="sticky left-0 bg-white z-10 px-2 py-1 text-left border-b border-r min-w-[120px] text-xs font-medium text-muted-foreground">
                            Habitación
                          </th>
                          {days.map((day) => {
                            const occ = getOccupancy(prop.id, day);
                            const today = isToday(day);
                            return (
                              <th
                                key={day.toISOString()}
                                className={`px-0 py-1 text-center border-b min-w-[44px] ${today ? 'bg-blue-100' : ''}`}
                              >
                                <div className="text-[10px] text-muted-foreground">{format(day, 'EEE', { locale: es })}</div>
                                <div className={`text-xs font-bold ${today ? 'text-blue-700' : ''}`}>{format(day, 'd')}</div>
                                <div className={`text-[9px] ${occ > 80 ? 'text-red-500' : occ > 50 ? 'text-amber-500' : 'text-green-500'}`}>
                                  {occ}%
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {data.rooms.map((room) => (
                          <tr key={room.id}>
                            <td className="sticky left-0 bg-white z-10 px-2 py-1 border-b border-r text-xs font-medium truncate max-w-[120px]">
                              {room.nombre || room.numero}
                            </td>
                            {days.map((day) => {
                              const reservation = getReservationForCell(room.id, day, data.reservations);
                              const today = isToday(day);
                              if (!reservation) {
                                return (
                                  <td key={day.toISOString()} className={`border-b border-r px-0 py-1 h-8 ${today ? 'bg-blue-50' : ''}`} />
                                );
                              }
                              const { isStart, isEnd } = getReservationSpan(reservation, day);
                              const isCuentaPorCobrar = reservation.enviado_a_credito === true;
                              const color = isCuentaPorCobrar ? '#dc2626' : (OTA_COLORS[reservation.ota_channel] || OTA_COLORS.default);
                              return (
                                <td
                                  key={day.toISOString()}
                                  className="border-b px-0 py-1 h-8 relative cursor-pointer hover:bg-slate-50"
                                  title={isCuentaPorCobrar ? `⚠️ CUENTAS POR COBRAR — ${reservation.guest_name || 'Huésped'}` : `${reservation.guest_name || 'Huésped'} — Click para ver detalle`}
                                  onClick={() => openReservationDetail(reservation, room, prop)}
                                  data-testid={`multi-cal-reservation-${reservation.id}-${format(day, 'yyyy-MM-dd')}`}
                                >
                                  <div
                                    className={`absolute inset-y-1 text-white text-[9px] font-medium flex items-center overflow-hidden hover:brightness-110 transition-all ${isCuentaPorCobrar ? 'ring-1 ring-red-300' : ''}`}
                                    style={{
                                      backgroundColor: color,
                                      left: isStart ? '2px' : 0,
                                      right: isEnd ? '2px' : 0,
                                      borderRadius: `${isStart ? '3px' : '0'} ${isEnd ? '3px' : '0'} ${isEnd ? '3px' : '0'} ${isStart ? '3px' : '0'}`,
                                    }}
                                  >
                                    {isStart && (
                                      <span className="truncate px-1">{reservation.guest_name || ''}</span>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                        {data.rooms.length === 0 && (
                          <tr>
                            <td colSpan={days.length + 1} className="text-center py-4 text-muted-foreground text-xs">
                              Sin habitaciones registradas
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-3 text-xs">
          <span className="text-muted-foreground font-medium">Canales:</span>
          {Object.entries(OTA_COLORS).filter(([k]) => k !== 'default').map(([key, color]) => (
            <span key={key} className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
              {key.charAt(0).toUpperCase() + key.slice(1)}
            </span>
          ))}
          <span className="flex items-center gap-1 ml-3 border-l pl-3">
            <div className="w-3 h-3 rounded bg-red-600 ring-1 ring-red-300" />
            <span className="text-red-700 font-medium">Cuentas por cobrar</span>
          </span>
        </div>

        {/* Reservation Detail Modal */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-lg" data-testid="multi-cal-reservation-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                <Calendar className="h-5 w-5 text-primary" />
                {detailReservation?.guest_name} {detailReservation?.guest_lastname || ''}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2 flex-wrap">
                <Badge className="capitalize">{detailReservation?.estado || '—'}</Badge>
                {detailReservation?.enviado_a_credito && (
                  <Badge className="bg-red-100 text-red-700 border-red-200">⚠️ Cuentas por cobrar</Badge>
                )}
                <span className="text-muted-foreground">•</span>
                <span className="text-xs">{detailProperty?.nombre}</span>
                <span className="text-muted-foreground">•</span>
                <span className="text-xs font-medium">{detailRoom?.nombre || detailRoom?.numero}</span>
              </DialogDescription>
            </DialogHeader>

            {detailReservation && (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <LogIn className="h-3.5 w-3.5" /> Check-in
                    </div>
                    <div className="font-semibold">{detailReservation.fecha_checkin ? format(parseISO(detailReservation.fecha_checkin), "d MMM yyyy", { locale: es }) : '—'}</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <LogOut className="h-3.5 w-3.5" /> Check-out
                    </div>
                    <div className="font-semibold">{detailReservation.fecha_checkout ? format(parseISO(detailReservation.fecha_checkout), "d MMM yyyy", { locale: es }) : '—'}</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 rounded-lg p-2.5 text-center">
                    <div className="text-[10px] text-blue-600 uppercase tracking-wide">Noches</div>
                    <div className="font-bold text-lg text-blue-900">
                      {detailReservation.fecha_checkin && detailReservation.fecha_checkout ? differenceInDays(parseISO(detailReservation.fecha_checkout), parseISO(detailReservation.fecha_checkin)) : 0}
                    </div>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-2.5 text-center">
                    <div className="text-[10px] text-emerald-600 uppercase tracking-wide">Total</div>
                    <div className="font-bold text-lg text-emerald-700">${(detailReservation.precio_total || 0).toLocaleString()}</div>
                  </div>
                  <div className="bg-violet-50 rounded-lg p-2.5 text-center">
                    <div className="text-[10px] text-violet-600 uppercase tracking-wide">Canal</div>
                    <div className="font-bold text-sm text-violet-700 capitalize truncate">{detailReservation.ota_channel || detailReservation.fuente || 'Directo'}</div>
                  </div>
                </div>

                <div className="border rounded-lg divide-y">
                  {detailReservation.guest_phone && (
                    <div className="flex items-center gap-2 px-3 py-2">
                      <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm">{detailReservation.guest_phone}</span>
                    </div>
                  )}
                  {detailReservation.guest_email && (
                    <div className="flex items-center gap-2 px-3 py-2">
                      <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm truncate">{detailReservation.guest_email}</span>
                    </div>
                  )}
                  {detailReservation.guest_documento && (
                    <div className="flex items-center gap-2 px-3 py-2">
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm">{detailReservation.guest_tipo_documento || 'Doc'}: {detailReservation.guest_documento}</span>
                    </div>
                  )}
                  {detailReservation.notas && (
                    <div className="px-3 py-2 bg-amber-50">
                      <div className="text-[10px] text-amber-700 uppercase font-semibold mb-0.5">Notas</div>
                      <div className="text-xs text-slate-700">{detailReservation.notas}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailOpen(false)} data-testid="multi-cal-modal-close">Cerrar</Button>
              <Button onClick={goToFullReservation} className="gap-1.5" data-testid="multi-cal-modal-open-calendar">
                Abrir en Calendario <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
